import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, Plus, Search, Loader2, Building2, User as UserIcon, Wallet, Tag, MessageCircle, Send,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { openWhatsApp } from "../../lib/whatsapp";
import { StatementBatchRunModal } from "../../components/admin/StatementBatchRunModal";

const TYPES = [
  { value: "", label: "All types" },
  { value: "individual", label: "Individual" },
  { value: "company", label: "Company / Fleet" },
  { value: "cod", label: "Once-off / COD" },
];

const TERMS_LABEL = { cod: "COD", "30_days": "30 days", "60_days": "60 days" };

export const AdminCustomers = () => {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [showBatchRun, setShowBatchRun] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(
        await adminApi.listCustomers({
          ...(q ? { q } : {}),
          ...(type ? { customer_type: type } : {}),
        })
      );
    } catch {
      toast.error("Could not load customers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const filteredTags = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => (r.tags || []).forEach((t) => set.add(t)));
    return Array.from(set);
  }, [rows]);

  const totals = useMemo(() => ({
    total: rows.length,
    companies: rows.filter((r) => r.customer_type === "company").length,
    individuals: rows.filter((r) => r.customer_type === "individual").length,
    cod: rows.filter((r) => r.customer_type === "cod").length,
  }), [rows]);

  return (
    <div data-testid="admin-customers-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Customers</h1>
          <p className="text-[#3f4b52] mt-1">
            Register accounts (companies, fleets, individuals) with a Pastel-style short code. Once-off / COD walk-ins can stay loose or be captured here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowBatchRun(true)}
            data-testid="statement-batch-open"
            className="bg-[#20282c] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110"
          >
            <Send size={14} /> Statement run
          </button>
          <Link
            to="/admin/customers/new"
            data-testid="customer-new-btn"
            className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110"
          >
            <Plus size={14} /> New customer
          </Link>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card icon={Users} label="Total" value={totals.total} color="#20282c" />
        <Card icon={Building2} label="Companies / Fleets" value={totals.companies} color="#3b82f6" />
        <Card icon={UserIcon} label="Individuals" value={totals.individuals} color="#10b981" />
        <Card icon={Wallet} label="Once-off / COD" value={totals.cod} color="#f28a16" />
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); load(); }}
        className="mt-6 flex flex-wrap items-center gap-3"
      >
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa6ad]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by code, name, email, ID, VAT number…"
            data-testid="customer-search"
            className="pl-9 bg-white border-[#cfd7db] h-10"
          />
        </div>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="h-10 bg-white border border-[#cfd7db] rounded-md px-3 text-sm"
          data-testid="customer-type-filter"
        >
          {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button
          type="submit"
          className="bg-[#20282c] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs"
        >
          Apply
        </button>
      </form>

      {filteredTags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 items-center text-xs text-[#3f4b52]">
          <Tag size={12} className="text-[#f28a16]" />
          {filteredTags.map((t) => <span key={t} className="bg-[#f28a16]/10 text-[#f28a16] px-2 py-0.5 rounded-sm">{t}</span>)}
        </div>
      )}

      <div className="mt-4 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#3f4b52] inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[#3f4b52] text-sm">
            No customers yet.{" "}
            <Link to="/admin/customers/new" className="text-[#f28a16] hover:underline">Create the first one →</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#edf2f4] text-[#3f4b52] uppercase tracking-widest text-xs">
                <tr>
                  <th className="text-left px-4 py-2.5">Code</th>
                  <th className="text-left px-4 py-2.5">Name</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-left px-4 py-2.5">Contact</th>
                  <th className="text-left px-4 py-2.5">Terms</th>
                  <th className="text-left px-4 py-2.5">Tags</th>
                  <th className="text-right px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eef2f4]">
                {rows.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => nav(`/admin/customers/${c.id}`)}
                    className="hover:bg-[#f5f6f6] cursor-pointer"
                    data-testid={`customer-row-${c.code}`}
                  >
                    <td className="px-4 py-3 font-mono font-bold text-[#f28a16]">{c.code}</td>
                    <td className="px-4 py-3">
                      <div className="font-display font-bold text-[#111619]">{c.name}</div>
                      {c.vat_number && <div className="text-xs text-[#3f4b52]">VAT {c.vat_number}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-[#3f4b52] uppercase tracking-wider inline-flex items-center gap-1">
                        {c.customer_type === "company" ? <Building2 size={12} /> : c.customer_type === "cod" ? <Wallet size={12} /> : <UserIcon size={12} />}
                        {c.customer_type === "company" ? "Company" : c.customer_type === "cod" ? "COD" : "Individual"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#3f4b52] text-xs">
                      {c.mobile && <div>{c.mobile}</div>}
                      {c.email && <div className="truncate max-w-[220px]">{c.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-[#3f4b52] text-xs">{TERMS_LABEL[c.account_terms] || c.account_terms}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).map((t) => (
                          <span key={t} className="bg-[#edf2f4] text-[#3f4b52] px-1.5 py-0.5 rounded-sm text-xs">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {c.mobile && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openWhatsApp(c.mobile, `Hi ${(c.name || "").split(" ")[0] || "there"}, this is Diesel & Dust Windscreens — `);
                            }}
                            className="text-[#25D366] hover:brightness-110 inline-flex items-center gap-1 text-xs"
                            data-testid={`whatsapp-customer-${c.code}`}
                            title="Open WhatsApp with this customer"
                          >
                            <MessageCircle size={13} />
                          </button>
                        )}
                        <span className={`px-2 py-0.5 rounded-sm text-xs font-display uppercase tracking-wider ${c.active ? "bg-[#10b981] text-white" : "bg-[#9aa6ad] text-white"}`}>
                          {c.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showBatchRun && (
        <StatementBatchRunModal onClose={() => setShowBatchRun(false)} />
      )}
    </div>
  );
};

const Card = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-[#cfd7db] rounded-md p-4">
    <div className="flex items-center justify-between mb-1.5">
      <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">{label}</div>
      <div className="h-8 w-8 rounded-md inline-flex items-center justify-center" style={{ background: `${color}1a`, color }}>
        <Icon size={16} />
      </div>
    </div>
    <div className="font-display font-black text-2xl text-[#111619]">{value}</div>
  </div>
);

export default AdminCustomers;
